import React, { useCallback, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  getStoreCheckoutPickStoreTypeTitle,
  STORE_CHECKOUT_PICK_STORE_TYPE_OPTIONS,
  STORES_FORM_MIXPANEL_SECTIONS,
} from '../constants/storeCheckout.constants';
import { mapPickStoreTypeToV4 } from '../helpers/storeInfoFormLegacy.utils';
import {
  isStoreCheckoutEditPage,
  resolveStoreCheckoutOrderIdFromUrl,
} from '../helpers/storeCheckoutStoreDetails.utils';
import { useUpdateStoreMutation } from '@/api/StoreQuery';
import { useStyleClass } from '@/hooks/useStyleClass';
import { captureStoresFormSectionCompleted } from '@/mixpanel/MixpanelEvents';
import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store';
import Icon from '@/components/Icon/Icon';

import './PickStoreType.scss';

export type StoreType = 'group-order' | 'brand-drop' | 'fundraiser' | 'permanent-store';

const PickStoreType: React.FC = () => {
  const styleClass = useStyleClass('pick-store-type', true);
  const { control, watch } = useFormContext();
  const [updateStore] = useUpdateStoreMutation();
  const { user } = useAppSelector((state: RootState) => state.user);

  const storeIdFromUrl = useMemo(() => resolveStoreCheckoutOrderIdFromUrl(), []);

  const selectedStoreType = watch('storeType') as StoreType;
  const isStoreTypeLocked = isStoreCheckoutEditPage();

  const visibleStoreTypeOptions = useMemo(
    () =>
      STORE_CHECKOUT_PICK_STORE_TYPE_OPTIONS.filter(
        (option) =>
          option.value !== 'permanent-store' ||
          (isStoreTypeLocked && selectedStoreType === 'permanent-store')
      ),
    [isStoreTypeLocked, selectedStoreType]
  );

  const handleStoreTypeSelect = useCallback(
    (nextStoreType: StoreType, onChange: (value: StoreType) => void) => {
      if (isStoreTypeLocked) {
        return;
      }

      onChange(nextStoreType);

      if (nextStoreType === selectedStoreType) {
        return;
      }

      captureStoresFormSectionCompleted(
        user?.email ?? '',
        STORES_FORM_MIXPANEL_SECTIONS.STORE_TYPE,
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Type: getStoreCheckoutPickStoreTypeTitle(nextStoreType),
        }
      );

      if (!storeIdFromUrl) {
        return;
      }

      updateStore({
        order_id: Number(storeIdFromUrl),
        store_type: mapPickStoreTypeToV4(nextStoreType),
      })
        .unwrap()
        .catch(() => {
          /* store type save is best-effort when store exists */
        });
    },
    [isStoreTypeLocked, selectedStoreType, storeIdFromUrl, updateStore, user?.email]
  );

  return (
    <div className={styleClass}>
      <div className={`${styleClass}__container`}>
        <div className={`${styleClass}__options`}>
          {visibleStoreTypeOptions.map((option) => (
            <Controller
              key={option.value}
              name="storeType"
              control={control}
              render={({ field }) => {
                const isSelected = selectedStoreType === option.value;
                const isReadonly = isStoreTypeLocked;

                return (
                  <div className={`${styleClass}__option-container`}>
                    <div
                      className={`${styleClass}__option ${option.className} ${
                        isSelected ? `${styleClass}__option--selected` : ''
                      } ${
                        isSelected && isStoreTypeLocked
                          ? `${styleClass}__option--selected-disabled`
                          : ''
                      } ${isReadonly ? `${styleClass}__option--readonly` : ''}`}
                      onClick={() => handleStoreTypeSelect(option.value, field.onChange)}
                      onKeyDown={(e) => {
                        if (!isStoreTypeLocked && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleStoreTypeSelect(option.value, field.onChange);
                        }
                      }}
                      role="button"
                      tabIndex={isStoreTypeLocked ? -1 : 0}
                      aria-disabled={isStoreTypeLocked}
                      data-testid={`store-type-option-${option.value}`}
                    >
                      <div className={`${styleClass}__option-header`}>
                        <div className={`${styleClass}__radio`}>
                          <input
                            type="radio"
                            id={`orderType_${option.value}`}
                            name="orderType"
                            value={option.value}
                            checked={isSelected}
                            onChange={() => handleStoreTypeSelect(option.value, field.onChange)}
                            disabled={isStoreTypeLocked}
                            className={`${styleClass}__radio-input`}
                            data-testid={`store-type-radio-${option.value}`}
                          />
                          <label
                            htmlFor={`orderType_${option.value}`}
                            className={`${styleClass}__radio-label`}
                          >
                            <div
                              className={`${styleClass}__radio-circle ${
                                isSelected ? `${styleClass}__radio-circle--selected` : ''
                              }`}
                            />
                          </label>
                        </div>
                        <div
                          className={`${styleClass}__option-title ${
                            isSelected ? `${styleClass}__option-title--selected` : ''
                          }`}
                        >
                          {option.title}
                        </div>
                        <div className={`${styleClass}__option-icon`}>
                          <Icon source={option.icon} width="24px" height="24px" />
                        </div>
                      </div>
                      <div className={`${styleClass}__option-description`}>
                        {option.description}
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

PickStoreType.displayName = 'PickStoreType';

export default PickStoreType;
